/**
 * Enhanced Product Information Types
 * 
 * Type definitions for rich product information including images, nutrition data,
 * and custom content for the enhanced batch creation feature.
 */

// ============================================================================
// CORE ENHANCED PRODUCT INFORMATION TYPES
// ============================================================================

export interface EnhancedProductInfo {
  productType?: ProductType;
  productImages?: ProductImageMetadata[];
  nutritionInfo?: NutritionInformation;
  customContent?: CustomProductContent;
  produceDetails?: ProduceDetails;
  manufacturingDetails?: ManufacturingDetails;
  labelDesign?: LabelDesign;
}

export interface EnhancedBatchCreationData {
  // Existing batch fields
  manufacturerId: string;
  productName: string;
  productCode: string;
  unitCode: string;
  eventDate: string;
  serialStart: number;
  serialEnd: number;
  productType?: ProductType;
  labelDesign?: LabelDesign;
  qrTokenFormat?: 'legacy' | 'short';
  qrTokenLength?: number;
  processVersion?: string;
  includeInnerQr?: boolean;
  
  // Enhanced information fields
  productImages?: string[]; // Array of image IDs
  nutritionInfo?: NutritionInformation;
  customContent?: CustomProductContent;
  produceDetails?: ProduceDetails;
  manufacturingDetails?: ManufacturingDetails;
}

export type ProductType = 'manufactured' | 'produce';

export interface LabelDesign {
  qrSizeInches?: number;
  fontFamily?: 'space_grotesk' | 'roboto_condensed';
  brandFontSize?: number;
  taglineFontSize?: number;
  textFontSize?: number;
  qrLabelFontSize?: number;
  lineHeight?: number;
  thermalLabelSize?: '2x1' | '2x1.5' | '2x2' | '50x30mm' | '50x34mm';
  layoutPreset?:
    | 'qr_left_text_right'
    | 'qr_right_text_left'
    | 'qr_top_text_bottom'
    | 'qr_left_logo_text_right';
  textAlign?: 'left' | 'center' | 'right';
  contentGapMm?: number;
  qrZoneRatio?: number;
  qrTextLayout?: 'stacked' | 'side';
  qrTextOrientation?: 'top_to_bottom' | 'bottom_to_top';
  qrDarkness?: number;
  textDarkness?: number;
  textBold?: boolean;
  qrTextBold?: boolean;
  logoUrl?: string;
  logoPath?: string;
  logoSizeMm?: number;
  logoWidthMm?: number;
  logoHeightMm?: number;
  brandName?: string;
  tagline?: string;
  website?: string;
  qrCaption?: string;
  qrBranding?: string;
  freeLines?: string[];
}

// ============================================================================
// PRODUCT IMAGES TYPES
// ============================================================================

export interface ProductImageMetadata {
  imageId: string;
  originalFilename: string;
  displayOrder: number;
  altText?: string;
  isPrimary: boolean;
}

export interface ProductImage {
  id: string;
  batchId: string;
  originalFilename: string;
  filePath: string;
  optimizedPath?: string;
  thumbnailPath?: string;
  fileSize: number;
  optimizedSize?: number;
  mimeType: string;
  width?: number;
  height?: number;
  uploadStatus: ImageUploadStatus;
  createdAt: Date;
  updatedAt: Date;
}

export type ImageUploadStatus = 'processing' | 'completed' | 'failed' | 'optimizing';

export interface ImageUploadResult {
  id: string;
  originalName: string;
  optimizedUrl: string;
  thumbnailUrl: string;
  fileSize: number;
  status: ImageUploadStatus;
}

export interface BatchTemplateImage {
  id: string;
  originalName: string;
  fileSize: number;
  mimeType?: string;
  downloadUrl: string;
  optimizedUrl?: string;
  thumbnailUrl?: string;
}

export interface EnhancedBatchTemplate {
  batchId: string;
  productType: ProductType;
  qrTokenFormat?: 'legacy' | 'short';
  qrTokenLength?: number;
  includeInnerQr?: boolean;
  nutritionInfo?: NutritionInformation;
  customContent?: CustomProductContent;
  produceDetails?: ProduceDetails;
  manufacturingDetails?: ManufacturingDetails;
  labelDesign?: LabelDesign;
  packagingData?: {
    unitsPerCase: number;
    casesPerPallet: number;
    caseDimensions?: string;
    palletDimensions?: string;
  };
  images: BatchTemplateImage[];
}

// ============================================================================
// NUTRITION INFORMATION TYPES
// ============================================================================

export interface NutritionInformation {
  servingSize: string;
  servingsPerContainer?: number;
  caloriesPerServing: number;
  caloriesPer100g?: number;
  
  // Macronutrients (per serving)
  macronutrients: MacronutrientInfo;
  
  // Vitamins and minerals (optional)
  vitamins?: VitaminContent[];
  minerals?: MineralContent[];
}

export interface MacronutrientInfo {
  protein: number;
  carbohydrates: number;
  totalFat: number;
  saturatedFat?: number;
  transFat?: number;
  fiber?: number;
  sugar?: number;
  sodium: number;
}

export interface VitaminContent {
  name: string;
  amount: number;
  unit: string;
  dailyValuePercent?: number;
}

export interface MineralContent {
  name: string;
  amount: number;
  unit: string;
  dailyValuePercent?: number;
}

// ============================================================================
// CUSTOM CONTENT TYPES
// ============================================================================

export interface CustomProductContent {
  ingredients?: string;
  allergens?: string[];
  usageInstructions?: string;
  storageInstructions?: string;
  customMessage?: string; // Rich text content
  brandStory?: string;
}

// ============================================================================
// MANUFACTURING DETAILS TYPES
// ============================================================================

export interface ManufacturingDetails {
  manufacturingDate?: Date;
  expiryDate?: Date;
  batchNumber?: string;
  facilityLocation?: string;
  mrp?: number;
}

export interface ProduceDetails {
  harvestDate?: Date;
  origin?: string;
  variety?: string;
  storageInstructions?: string;
   expiryDate?: Date;
   bestBeforeDays?: number;
}

// ============================================================================
// AUDIT TRAIL TYPES
// ============================================================================

export interface ProductInfoAudit {
  id: string;
  batchId: string;
  changeType: ProductInfoChangeType;
  oldValues?: any;
  newValues: any;
  changedBy?: string;
  changedAt: Date;
  changer?: {
    id: string;
    email: string;
    fullName: string;
  };
  batch?: {
    id: string;
    productName: string;
    batchTitle?: string;
    manufacturerName: string;
    createdAt?: Date;
  };
}

export interface ProductInfoVersion {
  id: string;
  batchId: string;
  version: number;
  data: any;
  createdBy: string;
  createdAt: Date;
  summary: {
    hasNutritionInfo: boolean;
    hasCustomContent: boolean;
    hasManufacturingDetails: boolean;
    imageCount: number;
    totalDataSize: number;
  };
}

export interface ProductInfoComparison {
  batchId: string;
  version1: number;
  version2: number;
  differences: ProductInfoDifference[];
  summary: {
    totalChanges: number;
    changedSections: string[];
    significantChanges: string[];
  };
}

export interface ProductInfoDifference {
  section: string;
  field: string;
  oldValue: any;
  newValue: any;
  significance: 'low' | 'medium' | 'high';
}

export interface AuditTrailQuery {
  batchId?: string;
  changeType?: ProductInfoChangeType;
  changedBy?: string;
  startDate?: Date;
  endDate?: Date;
  page?: number;
  limit?: number;
}

export interface AuditTrailResponse {
  auditTrail: ProductInfoAudit[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface RollbackRequest {
  batchId: string;
  targetVersion: number;
  rollbackBy: string;
  reason: string;
}

export interface RollbackResponse {
  success: boolean;
  batchId: string;
  rolledBackToVersion: number;
  newVersion: number;
  auditId: string;
  rollbackSummary: {
    nutritionInfoChanged: boolean;
    customContentChanged: boolean;
    manufacturingDetailsChanged: boolean;
    imageChanges: number;
  };
}

export type ProductInfoChangeType =
  | 'images'
  | 'nutrition'
  | 'custom_content'
  | 'manufacturing'
  | 'produce'
  | 'label_design'
  | 'rollback';

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

export interface EnhancedBatchCreationRequest {
  productName: string;
  productCode: string;
  unitCode: string;
  eventDate: string;
  serialStart: number;
  serialEnd: number;
  productType?: ProductType;
  labelDesign?: LabelDesign;
  qrTokenFormat?: 'legacy' | 'short';
  qrTokenLength?: number;
  processVersion?: string;
  includeInnerQr?: boolean;
  productImages?: File[];
  nutritionInfo?: NutritionInformation;
  customContent?: CustomProductContent;
  produceDetails?: ProduceDetails;
  manufacturingDetails?: ManufacturingDetails;
}

export interface EnhancedBatchCreationResponse {
  batchId: string;
  productName: string;
  batchTitle?: string;
  productCode?: string;
  unitCode?: string;
  processVersion?: string;
  eventDate?: Date;
  batchSequence?: number;
  serialStart: number;
  serialEnd: number;
  totalProducts: number;
  uploadedImages: ImageUploadResult[];
  enhancedInfoVersion: number;
  createdAt: Date;
}

export interface ProductInfoUpdateRequest {
  batchId: string;
  updateType: ProductInfoChangeType;
  data: Partial<EnhancedProductInfo>;
}

export interface ProductInfoUpdateResponse {
  success: boolean;
  batchId: string;
  updatedFields: string[];
  auditId: string;
  version: number;
}

// ============================================================================
// VALIDATION TYPES
// ============================================================================

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

export interface EnhancedProductInfoValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings?: ValidationError[];
}

// ============================================================================
// FILE UPLOAD TYPES
// ============================================================================

export interface FileUploadConstraints {
  maxFileSize: number; // 5MB in bytes
  maxFiles: number; // 5 images max
  allowedFormats: readonly string[]; // ['image/jpeg', 'image/png', 'image/webp']
  minDimensions: { width: number; height: number };
  maxDimensions: { width: number; height: number };
}

export interface ImageProcessingOptions {
  generateThumbnail: boolean;
  thumbnailSize: { width: number; height: number };
  optimizeForWeb: boolean;
  quality: number; // 0-100
  format?: 'jpeg' | 'png' | 'webp';
}

// ============================================================================
// DISPLAY TYPES FOR QR VERIFICATION
// ============================================================================

export interface EnhancedProductDisplay {
  productName: string;
  serialNumber: number;
  productType?: ProductType;
  batchInfo: {
    batchId: string;
    manufacturerName: string;
    createdAt: Date;
  };
  images: ProductImageDisplay[];
  nutritionInfo?: NutritionInformation;
  customContent?: CustomProductContent;
  produceDetails?: ProduceDetails;
  manufacturingDetails?: ManufacturingDetails;
}

export interface ProductImageDisplay {
  id: string;
  url: string;
  thumbnailUrl: string;
  altText?: string;
  isPrimary: boolean;
  displayOrder: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const ENHANCED_PRODUCT_INFO_CONSTANTS = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_FILES: 5,
  ALLOWED_MIME_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
  THUMBNAIL_SIZE: { width: 300, height: 300 },
  MAX_DIMENSIONS: { width: 4096, height: 4096 },
  MIN_DIMENSIONS: { width: 100, height: 100 },
  CURRENT_SCHEMA_VERSION: 1,
  
  // Text length limits
  MAX_CUSTOM_MESSAGE_LENGTH: 2000,
  MAX_INGREDIENTS_LENGTH: 1000,
  MAX_INSTRUCTIONS_LENGTH: 1500,
  MAX_BRAND_STORY_LENGTH: 3000,
  MAX_TEXT_LENGTH: 5000, // General max text length for sanitization
  
  // Nutrition validation
  MAX_CALORIE_VALUE: 9999,
  MAX_NUTRIENT_VALUE: 999.99,
  MIN_SERVING_SIZE_LENGTH: 1,
  MAX_SERVING_SIZE_LENGTH: 50,
  
  // Security and validation constants
  MAX_STORAGE_PER_MANUFACTURER: 1024 * 1024 * 1024, // 1GB per manufacturer (increased from 100MB)
  MAX_FILES_PER_MANUFACTURER: 5000, // 5000 files per manufacturer (increased from 500)
  
  // Rich text sanitization
  ALLOWED_HTML_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 'ol', 'ul', 'li', 
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span'
  ],
  ALLOWED_HTML_ATTRIBUTES: {
    '*': ['class'],
    'p': ['style'],
    'span': ['style'],
    'h1': ['style'],
    'h2': ['style'],
    'h3': ['style'],
    'h4': ['style'],
    'h5': ['style'],
    'h6': ['style']
  },
  
  // Rate limiting
  UPLOAD_RATE_LIMIT_WINDOW: 15 * 60 * 1000, // 15 minutes
  UPLOAD_RATE_LIMIT_MAX: 20, // 20 uploads per window
  UPDATE_RATE_LIMIT_WINDOW: 5 * 60 * 1000, // 5 minutes
  UPDATE_RATE_LIMIT_MAX: 10, // 10 updates per window
} as const;
